<template>
  <nav class="navigator">
    <strong class="navigator__logo">
      <router-link
        class="navigator__logo-link"
        to="/"
        @mouseenter.native="$eventHub.$emit('morphCursor', 'circle')"
        @mouseleave.native="$eventHub.$emit('morphCursor', 'rect')"
      >
        <svg xmlns="http://www.w3.org/2000/svg" x="0" y="0" viewBox="0 0 55 31">
          <polygon points="43,0 33.3,30.9 26.7,30.9 21.5,10.9 16.3,31
            9.7,31 0,0.1 7.6,0.1 13,21.3 18.1,0.1 25,0.1 30.1,21.3 35.5,0.1 "/>
          <path d="M50,21c2.9,0,5,2.1,5,5s-2.1,5-5,5s-5-2.1-5-5S47.2,21,50,21z"/>
        </svg>
      </router-link>
    </strong>
    <div
      :class="[
        'navigator__container',
        $store.state.isMenuOpen ? 'navigator__container--open' : '',
      ]"
      ref="menuContainer"
    >
      <ul class="navigator__list" ref="nav">
        <li
          class="navigator__item"
          v-for="(voice, i) in navigatorVoices"
          :key="`navigatorVoice-${i}`"
        >
          <router-link
            class="navigator__link"
            :to="`/${$route.params.city}${voice.url}`"
            @mouseenter.native="$eventHub.$emit('morphCursor', 'circle')"
            @mouseleave.native="$eventHub.$emit('morphCursor', 'rect')"
          >
            {{ voice.content }}
          </router-link>
          <AppLine class="app__line" :isAniamted="false"/>
        </li>
      </ul>
    </div>
    <AppButton
      class="navigator__caption"
      theme="light"
      :data="{
        content: 'Buy Tickets',
        url: currentLinkUniverse,
      }"
      :router="false"
      :hasIcon="checkIcon()"
      :inMenu="true"
    />
    <NavigatorBurger
      aria-label="navigator button"
      class="navigator__burger"
      @click.native="openMenu"
    />
  </nav>
</template>

<script>
import { mapGetters } from 'vuex';
import NavigatorBurger from '@/components/NavigatorBurger.vue';
import AppButton from '@/components/AppButton.vue';
import AppLine from '@/components/AppLine.vue';

import hades from '@/scripts/hades';
import raf from '@/scripts/raf';
import { initials, animations } from '@/scripts/fx';
import { isMobile } from '../scripts/utils';

export default {
  name: 'Navigator',
  components: {
    NavigatorBurger,
    AppButton,
    AppLine,
  },
  data() {
    return {
      menuAnimating: false,
      navigator: [
        { content: 'Home .', url: '/' },
        { content: 'Speakers .', url: '/speakers' },
        { content: 'Schedule .', url: '/schedule' },
        { content: 'Dev sessions .', url: '/dev-sessions', exclude: 'amsterdam' },
        { content: 'Workshops .', url: '/workshops' },
        { content: 'Activities .', url: '/activities' },
        { content: 'Venues .', url: '/venues' },
      ],
    };
  },
  computed: {
    ...mapGetters(['currentLinkUniverse']),
    navigatorVoices() {
      const index = this.navigator.findIndex(el => el.exclude === this.$route.params.city);
      if (index > -1) this.navigator.splice(index, 1); // eslint-disable-line
      return this.navigator;
    },
  },
  mounted() {
    raf.add(() => this.checkPositionMenu(), 'check-menuPosition', false);
    this.enter();
  },
  methods: {
    openMenu() {
      if (this.menuAnimating) return;
      if (this.$refs.menuContainer === undefined) return;
      this.menuAnimating = true;
      if (!this.$store.state.isMenuOpen) {
        animations.get('menu-open')(this.$refs.menuContainer).finished.then(() => { this.menuAnimating = false; });
      } else {
        animations.get('menu-close')(this.$refs.menuContainer).finished.then(() => { this.menuAnimating = false; });
      }
      this.$store.commit('SET_MENU_STATE', !this.$store.state.isMenuOpen);
    },
    checkPositionMenu() {
      if (this.$refs.nav === undefined) return;

      if (window.innerWidth < 768) {
        if (this.$refs.nav.classList.contains('nav--hidden')) this.$refs.nav.classList.remove('nav--hidden');
      } else if (window.innerWidth >= 768 && hades.ref.direction.y === 1 && this.$refs.nav.classList.contains('nav--hidden')) {
        this.$refs.nav.classList.remove('nav--hidden');
      } else if (window.innerWidth >= 768 && hades.ref.direction.y === -1 && !this.$refs.nav.classList.contains('nav--hidden')) {
        this.$refs.nav.classList.add('nav--hidden');
      }
    },
    checkIcon() {
      return window.innerWidth >= 768;
    },
    enter() {
      const logo = this.$el.querySelector('.navigator__logo');
      const menu = this.$el.querySelector('.navigator__container');
      const button = this.$el.querySelector('.navigator__caption');
      initials.get('fade')(logo, -60);
      initials.get('button')(button);
      animations.get('fade')(logo, 500);
      animations.get('button')(button, 800);
      if (!isMobile()) {
        initials.get('fade')(menu, -60);
        animations.get('fade')(menu, 500);
      } else {
        initials.get('menu')(menu);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.navigator {
  position: fixed;
  top: $unit-vh;
  left: 0;
  width: 100%;

  display: grid;
  grid-template-columns: repeat(16, 1fr);
  z-index: 30;

  @include mq(ipadL) {
    top: $unit-vh * 2;
  }

  @include mq($until: ipadL) {
    width: 100vw;
    padding: $unit-vh * 1.5 20px;
    background-color: var(--color-bg);
    top: 0;

    &::after {
      content: '';

      position: absolute;
      width: calc(100% - 40px);
      height: 1px;
      left: 20px;

      background-color: $color-white;
    }

    &::after {
      bottom: 0;
    }
  }

  &__logo {
    grid-column: 1 / 4;
    align-self: center;

    @include mq(ipadL) {
      grid-column: 1 / 3;
      place-self: center;
    }

    svg {
      display: inline-block;
      width: 40px;

      @include mq($from: dm) {
        width: 60px;
      }

      @include mq($from: dxl) {
        width: 80px;
      }
    }

    polygon {
      fill: var(--color-headline-dark);
      transition: fill 1s $outBack;
    }

    path {
      fill: var(--color-headline-light);
      transition: fill 1s $outBack;
    }
  }

  &__container {
    @include mq($until: ipadL) {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100vh;
      height: calc(var(--vh, 1vh) * 100);
      padding: $unit-vh * 6 20px 20px 20px;

      display: flex;
      align-items: flex-end;
      justify-content: flex-start;

      background-color: var(--color-bg);

      z-index: -1;
    }

    @include mq(ipadL) {
      display: inline-block;
      grid-column: 3 / 13;
    }

    .app__line {
      margin: $unit-vh * 0.2 0;
    }
  }

  &__list {
    display: flex;
    align-items: flex-start;
    justify-content: flex-start;
    flex-flow: column nowrap;
    width: 100%;

    @include mq(ipadL) {
      width: unset;
      align-items: center;
      justify-content: space-between;
      flex-flow: row wrap;

      margin-right: $unit-vw * 2;
      border-bottom: 1px solid rgba($color-white, 0.4);

      transition: transform 0.8s $inOutCirc,
        opacity 0.8s $inOutCirc;

      &.nav--hidden {
        transform: translateY(-80px);
        opacity: 0;
        pointer-events: none;
      }
    }

    .navigator__item {
      &:last-child {
        padding-right: 20px;
      }

      @include mq(ipadL) {
        .app__line {
          display: none;
        }
      }

      @include mq($until: ipadL) {
        width: 100%;

        @include fluid-type(
          375px,
          1024px,
          30px,
          135px,
          30px,
          135px
        );

        &:last-child {
          padding-right: 0;
        }

        &:nth-child(odd) a {
          color: var(--color-headline-light);
        }

        &:nth-child(even) a {
          color: var(--color-headline-dark);
        }
      }
    }
  }

  &__link {
    display: inline-block;
    position: relative;
    padding: $unit-vh 0;

    flex: 0 0 auto;

    font-size: 30px;
    line-height: 35px;

    @include mq($from: ipadL) {
      font-size: 16px;
      line-height: 24px;
    }

    @include mq($from: dm) {
      font-size: 20px;
      line-height: 20px;
    }

    @include mq($from: dxl) {
      font-size: 24px;
      line-height: 24px;
    }

    @include mq($until: ipadP) {
      padding: 10px 0;
    }

    &::before {
      content: '';

      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 1px;
      background-color: $color-white;

      transform: scaleX(0);
      transform-origin: 50% 50%;
      transition: transform 0.35s $outCirc;

      @include mq($until: ipadL) {
        display: none;
      }
    }

    &.router-link-exact-active {
      pointer-events: none;

      @include mq($until: ipadP) {
        @at-root [data-city="toronto"] & {
          color: #48C5B6 !important;
        }

        @at-root [data-city="amsterdam"] & {
          color: $color-white !important;
        }
      }

      @include mq(ipadP) {
        &::before {
          transform: scaleX(1);
          transform-origin: 50% 50%;
          transition: transform 0.35s $outBack;
        }
      }
    }
  }

  &__caption {
    position: fixed;
    top: $unit-vh;
    right:  calc(#{$unit-vw * 4} + 50px);

    @include mq($until: ipadL) {
      &::v-deep.button--no-icon a {
        padding: 13px 20px 13px 20px;
      }
    }

    @include mq(ipadL) {
      top: calc(#{$unit-vh * 2} + 4px);
      left: unset;
      right: $unit-vw * 4;
      transform: none;
    }
  }

  &__burger {
    align-self: center;
    justify-self: flex-end;
    grid-column: 15 / -1;

    @include mq(ipadL) {
      display: none;
    }
  }
}
</style>
